/* eslint-disable prefer-destructuring */
/* eslint-disable no-case-declarations */
import {
  EVENT_CHANGE_STATUS_SUCCESS,
  EVENT_PATCH_SUCCESS,
  EVENT_SUCCESS,
} from '@pingum/app-state-management/event/types';
import { uniqBy } from 'lodash';
import { formatDateString } from '../../utils/dateHelper';
import * as types from './types';
import withSaveReconciliation from './saveReconciliation';
import { matchesBoardItem } from './boardItemMatches';
import * as horseTypes from '../horse/types';
import * as scheduleTypes from '../schedule/types';
import { generateEventsFromSchedules } from '../../utils/scheduleHelper';
import {
  getMappedHorseSuggestions,
  top5MostFrequent,
} from '../../utils/barnBoardHelper';

const upsertItemList = (items, item) => {
  const itemExists = items.find((t) => t.id === item.id);
  if (itemExists) {
    return uniqBy(items.map((t) => (t.id === item.id
      ? JSON.parse(JSON.stringify(item))
      : t)), 'id');
  }
  return uniqBy([...items, item], 'id');
};

const itemInFilter = (task, tagConditions, startDate, endDate) => {
  // tags filter
  if (tagConditions
    && tagConditions.length !== 0 && task.tags) {
    for (let i = 0; i < tagConditions.length; i += 1) {
      if (tagConditions[i].length !== 0) {
        let foundMatchingTag = false;
        for (let j = 0; j < task.tags.length; j += 1) {
          if (tagConditions[i].indexOf(task.tags[j].id) >= 0) {
            foundMatchingTag = true;
            break;
          }
        }
        if (!foundMatchingTag) {
          return false;
        }
      }
    }
  }

  // dates filter
  if (startDate || endDate) {
    const start = startDate
      ? new Date(startDate)
      : new Date(0);
    const end = endDate
      ? new Date(endDate)
      : new Date(8640000000000000);
    if ((new Date(task.startDate).getTime() >= start.getTime()
      && new Date(task.startDate).getTime() < end.getTime())
      || (!task.endDate && new Date(task.startDate).getTime() < start.getTime())
      || (task.endDate && new Date(task.startDate).getTime() < start.getTime() && new Date(task.endDate).getTime() > start.getTime())) {
      return true;
    }
  } else {
    return true;
  }

  return false;
};

const updateBoardTask = (state, task) => {
  if (!task?.id) return state;
  const knownTask = [...state.tasks, ...state.incompleteTasks].some((item) => Number(item.id) === Number(task.id));
  if (!knownTask && (!state.boardDataScopeKey || (state.isFetchingBoard && !state.isRefreshingBoard))) return state;
  const current = matchesBoardItem(task, state, 'tasks');
  const incomplete = !current && matchesBoardItem(task, state, 'incompleteTasks');
  return {
    ...state,
    lastUpdatedDate: new Date(),
    tasks: current
      ? upsertItemList(state.tasks, task)
      : state.tasks.filter((item) => Number(item.id) !== Number(task.id)),
    incompleteTasks: incomplete
      ? upsertItemList(state.incompleteTasks, task)
      : state.incompleteTasks.filter((item) => Number(item.id) !== Number(task.id)),
  };
};

const reducer = (state = {
  startDate: new Date(),
  endDate: new Date(),
  tagConditions: [],
  lastUpdatedDate: new Date(),
  location: null,
  barnHorses: [], // we won't page for the barn board since we need a full list
  tasks: [], // we won't page for the barn board since we need a full list
  incompleteTasks: [], // still active tasks before date range
  events: [], // all events scheduled for date range
  schedules: [],
  scheduleExceptions: [],
  isFetchingBoard: false,
  isRefreshingBoard: false,
  errorMessage: null,
  refreshErrorMessage: null,
  realTimeUpdateError: '',
  boardSearchKey: '', // A key (startDate + endDate + locationId) to tell us if horse data needs to be loaded again
  isFetchingBoardSuggestions: false,
  upcomingDueDates: [],
  horseSuggestions: [],
  mostUsedTaskTypes: [],
  planningDay: null,
}, action = {}) => {
  switch (action.type) {
    case types.SET_BOARD_PLANNING_DAY:
      return {
        ...state,
        planningDay: action.planningDay,
      };
    case types.GET_BOARD_SUGGESTION_REQUESTED:
      return {
        ...state,
        suggestionRequestId: action.requestId,
        isFetchingBoardSuggestions: true,
        upcomingDueDates: [],
        horseSuggestions: [],
      };
    case types.GET_BOARD_SUGGESTION_SUCCESS:
      if (action.requestId !== state.suggestionRequestId) { return state; }
      return {
        ...state,
        isFetchingBoardSuggestions: false,
        upcomingDueDates: action.upcomingDueDates,
        reminderDueDateCutoff: action.reminderDueDateCutoff,
        horseSuggestions: action.horseSuggestions,
        mappedHorseSuggestions: getMappedHorseSuggestions(action.horseSuggestions),
        mostUsedTaskTypes: top5MostFrequent(action.horseSuggestions),
      };
    case types.GET_BOARD_SUGGESTION_ERROR:
      if (action.requestId !== state.suggestionRequestId) { return state; }
      return {
        ...state,
        isFetchingBoardSuggestions: false,
        upcomingDueDates: [],
        horseSuggestions: [],
        mostUsedTaskTypes: [],
      };
    case horseTypes.UPDATE_HORSE_SUGGESTION_SUCCESS:
      if (state.mappedHorseSuggestions
        && state.mappedHorseSuggestions[action.updatedHorseSuggestion.pingumTagId] && action.updatedHorseSuggestion.hideAllSuggestions) {
        return {
          ...state,
          mappedHorseSuggestions: {
            ...state.mappedHorseSuggestions,
            [action.updatedHorseSuggestion.pingumTagId]: null,
          },
        }
      }
      return state;
    case types.GET_BOARD_REQUESTED:
      return {
        ...state,
        boardDataScopeKey: action.background
          ? state.boardDataScopeKey
          : null,
        boardRequestId: action.requestId,
        isFetchingBoard: true,
        isRefreshingBoard: action.background === true,
        errorMessage: null,
        refreshErrorMessage: null,
        tagConditions: action.background
          ? state.tagConditions
          : [],
        startDate: action.startDate,
        endDate: action.endDate,
        realTimeUpdateError: '',
        tasks: action.background
          ? state.tasks
          : [],
        incompleteTasks: action.background
          ? state.incompleteTasks
          : [],
        events: action.background
          ? state.events
          : [],
      };
    case types.GET_BOARD_SUCCESS:
      if (action.requestId != null && action.requestId !== state.boardRequestId) return state;
      return {
        ...state,
        tagConditions: action.tagConditions,
        lastUpdatedDate: new Date(),
        isFetchingBoard: false,
        isRefreshingBoard: false,
        errorMessage: null,
        refreshErrorMessage: null,
        barnHorses: action.barnHorses,
        tasks: action.tasks,
        incompleteTasks: action.incompleteTasks,
        events: action.events,
        schedules: action.schedules,
        scheduleExceptions: action.scheduleExceptions,
        boardSearchKey: action.boardSearchKey,
        boardDataScopeKey: action.dataScopeKey,
        location: action.location,
      };
    case types.GET_BOARD_ERROR:
      if (action.requestId != null && action.requestId !== state.boardRequestId) return state;
      if (state.isRefreshingBoard) {
        return {
          ...state,
          isFetchingBoard: false,
          isRefreshingBoard: false,
          refreshErrorMessage: action.message || 'Unable to refresh the planner. Try again.',
        };
      }
      return {
        ...state,
        isFetchingBoard: false,
        isRefreshingBoard: false,
        errorMessage: action.message || null,
        boardSearchKey: '',
        barnHorses: [],
        location: null,
      };

    case EVENT_PATCH_SUCCESS:
      return updateBoardTask(state, action.updatedEvent);
    case EVENT_CHANGE_STATUS_SUCCESS:
      if (!action.newStatus || ![...state.tasks, ...state.incompleteTasks].some((task) => Number(task.id) === Number(action.eventId))) return state;
      const updateStatus = (task) => {
        if (Number(task.id) !== Number(action.eventId)) return task;
        const completeDate = action.newStatus.completeEvent
          ? task.completeDate || new Date()
          : task.completeDate;
        return {
          ...task,
          completeDate,
          currentEventStatus: action.newStatus,
          eventStatus: [...(task.eventStatus || []).filter((status) => status.id !== action.newStatus.id), action.newStatus],
        };
      };
      return updateBoardTask(state, updateStatus([...state.tasks, ...state.incompleteTasks]
        .find((task) => Number(task.id) === Number(action.eventId))));

    case types.BOARD_TASK_UPDATE:
      return updateBoardTask(state, action.task.event);
    case EVENT_SUCCESS:
      return updateBoardTask(state, action.event);
    case types.BOARD_TASK_UPDATE_ERROR:
      return {
        ...state,
        realTimeUpdateError: `ATTENTION: Real time board updates disabled, last updated ${formatDateString(state.lastUpdatedDate)}.`,
      };
    case types.BOARD_SCHEDULE_ADDED_UPDATE:
      // only process if the schedule is in filters and not already in the list of schedules
      if (itemInFilter(action.schedule, state.tagConditions, state.startDate, state.endDate)
        && !state.schedules.find((s) => s.id === action.schedule.id)) {
        const newSchedulesList = upsertItemList(state.schedules, action.schedule)
        return {
          ...state,
          lastUpdatedDate: new Date(),
          schedules: newSchedulesList,
          events: generateEventsFromSchedules(
            newSchedulesList,
            state.scheduleExceptions,
            state.startDate,
            state.endDate,
            action.barnUsersMap,
            action.barnMetadata,
            action.mappedStatuses || {},
          ),
        };
      }
      return { ...state }
    case types.BOARD_SCHEDULE_CHANGED_UPDATE:
      const updatedSchedulesList = upsertItemList(state.schedules, action.schedule);
      return {
        ...state,
        lastUpdatedDate: new Date(),
        schedules: updatedSchedulesList,
        events: generateEventsFromSchedules(
          updatedSchedulesList,
          state.scheduleExceptions,
          state.startDate,
          state.endDate,
          action.barnUsersMap,
          action.barnMetadata,
          action.mappedStatuses || {},
        ),
      };
    case types.BOARD_SCHEDULE_CANCELED_UPDATE:
      // remove the schedule, or any children schedules if in list
      const updatedRemovedSchedulesList = state.schedules
        .filter((s) => s.id !== action.schedule.id && (!s.parentSchedule || !s.parentSchedule.id || s.parentSchedule.id !== action.schedule.id));
      return {
        ...state,
        lastUpdatedDate: new Date(),
        schedules: updatedRemovedSchedulesList,
        events: generateEventsFromSchedules(
          updatedRemovedSchedulesList,
          state.scheduleExceptions,
          state.startDate,
          state.endDate,
          action.barnUsersMap,
          action.barnMetadata,
          action.mappedStatuses || {},
        ),
      };
    case types.BOARD_SCHEDULE_EXCEPTION_ADDED_UPDATE:
      // only process if the exception is associated with a schedule in the list, and the exception is not already in exception list
      if (state.schedules.find((s) => s.id === action.scheduleException.schedule.id) && !state.scheduleExceptions
        .find((se) => se.id === action.scheduleException.id)) {
        const updatedAddedScheduleExceptionsList = [...state.scheduleExceptions, action.scheduleException];
        return {
          ...state,
          scheduleExceptions: updatedAddedScheduleExceptionsList,
          events: generateEventsFromSchedules(
            state.schedules,
            updatedAddedScheduleExceptionsList,
            state.startDate,
            state.endDate,
            action.barnUsersMap,
            action.barnMetadata,
            action.mappedStatuses || {},
          ),
        };
      }
      return { ...state }
    case types.BOARD_SCHEDULE_EXCEPTION_CHANGED_UPDATE:
      // only process if the exception is in the list
      if (state.scheduleExceptions.find((se) => se.id === action.scheduleException.id)) {
        const updatedExceptionsList = state.scheduleExceptions.map((se) => (se.id !== action.scheduleException.id
          ? se
          : action.scheduleException));
        return {
          ...state,
          scheduleExceptions: updatedExceptionsList,
          events: generateEventsFromSchedules(
            state.schedules,
            updatedExceptionsList,
            state.startDate,
            state.endDate,
            action.barnUsersMap,
            action.barnMetadata,
            action.mappedStatuses || {},
          ),
        };
      }
      return { ...state }
    case types.BOARD_SCHEDULE_EXCEPTION_CANCELED_UPDATE:
      return {
        ...state,
        scheduleExceptions: state.scheduleExceptions.filter((se) => se.id !== action.scheduleException.id),
        events: generateEventsFromSchedules(
          state.schedules,
          state.scheduleExceptions.filter((se) => se.id !== action.scheduleException.id),
          state.startDate,
          state.endDate,
          action.barnUsersMap,
          action.barnMetadata,
          action.mappedStatuses || {},
        ),
      };
    case horseTypes.ADD_HORSE_NOTE_SUCCESS:
      if (action.pinToHorse) {
        return {
          ...state,
          barnHorses: state.barnHorses.map((bh) => (bh.horse.id === action.horseId
            ? {
              ...bh,
              horse: {
                ...bh.horse,
                activeNote: action.newHorseNote,
              },
            }
            : bh)),
        };
      }
      return { ...state };
    case horseTypes.UPDATE_HORSE_NOTE_SUCCESS:
      if (!action.pinToHorse) {
        return {
          ...state,
          barnHorses: state.barnHorses.map((bh) => (bh.horse.id === action.horseId
            ? {
              ...bh,
              horse: {
                ...bh.horse,
                activeNote: (action.pinToHorse === false && bh.horse.activeNote && bh.horse.activeNote.id === action.updatedHorseNote.id)
                  ? null
                  : bh.horse.activeNote,
              },
            }
            : bh)),
        };
      }
      return {
        ...state,
        barnHorses: state.barnHorses.map((bh) => (bh.horse.id === action.horseId
          ? {
            ...bh,
            horse: {
              ...bh.horse,
              activeNote: (action.pinToHorse)
                ? action.updatedHorseNote
                : bh.horse.activeNote,
            },
          }
          : bh)),
      };
    case horseTypes.DELETE_HORSE_NOTE_SUCCESS:
      return {
        ...state,
        barnHorses: state.barnHorses.map((bh) => (bh.horse.id === action.horseId
          ? {
            ...bh,
            horse: {
              ...bh.horse,
              activeNote: bh.horse.activeNote && bh.horse.activeNote.id === action.deletedHorseNoteId
                ? null
                : bh.horse.activeNote,
            },
          }
          : bh)),
      };

    /* SCHEDULE UPDATES */
    case scheduleTypes.ADD_SCHEDULE_SUCCESS:
      const newSchedules = upsertItemList(state.schedules, action.newSchedule);
      return {
        ...state,
        schedules: newSchedules,
        lastUpdatedDate: new Date(),
        events: generateEventsFromSchedules(
          newSchedules,
          state.scheduleExceptions,
          state.startDate,
          state.endDate,
          action.barnUsersMap,
          action.barnMetadata,
        ),
        mostUsedTaskTypes: [...state.mostUsedTaskTypes, action.newSchedule.eventTemplate.id].slice(0, 7), // add most recently used to list
      };
    case scheduleTypes.UPDATE_SCHEDULE_SUCCESS:
      const updatedSchedules = upsertItemList(state.schedules, action.updatedSchedule);
      return {
        ...state,
        lastUpdatedDate: new Date(),
        events: generateEventsFromSchedules(
          updatedSchedules,
          state.scheduleExceptions,
          state.startDate,
          state.endDate,
          action.barnUsersMap,
          action.barnMetadata,
          action.mappedStatuses || {},
        ),
        schedules: updatedSchedules,

      };
    case scheduleTypes.DELETE_SCHEDULE_SUCCESS:
      return {
        ...state,
        lastUpdatedDate: new Date(),
        events: state.events.filter((e) => e.resource.id !== action.deletedScheduleId
          && (!e.resource.parentSchedule || e.resource.parentSchedule.id !== action.deletedScheduleId)),
        schedules: state.schedules.filter((f) => f.id !== action.deletedScheduleId
          && (!f.parentSchedule || f.parentSchedule.id !== action.deletedScheduleId)),
      };
    case scheduleTypes.ADD_SCHEDULE_EXCEPTION_SUCCESS:
      // only add if not already in the list
      if (!state.scheduleExceptions.find((se) => se.id === action.newScheduleException.id)) {
        const newScheduleExceptions = [...state.scheduleExceptions, action.newScheduleException];
        const newSchedulesFromException = action.newChildSchedule
          ? [...state.schedules, action.newChildSchedule]
          : state.schedules;
        return {
          ...state,
          lastUpdatedDate: new Date(),
          schedules: newSchedulesFromException,
          events: generateEventsFromSchedules(
            newSchedulesFromException,
            newScheduleExceptions,
            state.startDate,
            state.endDate,
            action.barnUsersMap,
            action.barnMetadata,
            action.mappedStatuses || {},
          ),
          scheduleExceptions: newScheduleExceptions,
        };
      }
      return { ...state }
    case scheduleTypes.UPDATE_SCHEDULE_EXCEPTION_SUCCESS:
      if (state.scheduleExceptions.find((se) => se.id === action.updatedScheduleException.id)) {
        const updatedScheduleExceptions = state.scheduleExceptions.map((se) => (se.id === action.updatedScheduleException.id
          ? action.updatedScheduleException
          : se));
        return {
          ...state,
          lastUpdatedDate: new Date(),
          events: generateEventsFromSchedules(
            state.schedules,
            updatedScheduleExceptions,
            state.startDate,
            state.endDate,
            action.barnUsersMap,
            action.barnMetadata,
            action.mappedStatuses || {},
          ),
          scheduleExceptions: updatedScheduleExceptions,
        };
      }
      return { ...state }
    case scheduleTypes.DELETE_SCHEDULE_EXCEPTION_SUCCESS:
      const newScheduleExceptions2 = state.scheduleExceptions.filter((f) => f.id !== action.deletedScheduleExeptionId);
      return {
        ...state,
        lastUpdatedDate: new Date(),
        events: generateEventsFromSchedules(
          state.schedules,
          newScheduleExceptions2,
          state.startDate,
          state.endDate,
          action.barnUsersMap,
          action.barnMetadata,
          action.mappedStatuses || {},
        ),
        scheduleExceptions: newScheduleExceptions2,
      };
    default:
      return state;
  }
};

export default withSaveReconciliation(reducer);
